import { Box, Stack, Tooltip } from '@mui/material'
import { C1IconButton, TypogaphyVariantType } from 'c1ui'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1TextWithTruncation } from 'components/core/TextWithTruncation/C1TextWithTruncation'
import { Check, Copy } from 'lucide-react'

import { FC, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

type BaseC1CopyTextProps = {
    typographyVariant?: TypogaphyVariantType
    emptyText?: React.ReactElement
    truncateWidth?: string
    transparentButton?: boolean
    noTruncation?: boolean
}

type StringValueProps = {
    value?: string
    strValue?: string
}

type NonStringValueProps = {
    value: Exclude<React.ReactNode, string>
    strValue: string
}

export type C1CopyTextProps = BaseC1CopyTextProps & (StringValueProps | NonStringValueProps)

export const C1CopyText: FC<C1CopyTextProps> = ({
    value,
    strValue,
    typographyVariant = 'body1',
    emptyText,
    truncateWidth,
    transparentButton = false,
    noTruncation = false,
}) => {
    const intl = useIntl()

    const [clicked, setClicked] = useState(false)

    const textToCopy = strValue || (typeof value === 'string' ? value : '')

    const handleClick = (text: string | undefined): void => {
        if (!text) return
        navigator.clipboard.writeText(text)
        setClicked(true)
        setTimeout(() => setClicked(false), 3000)
    }

    return value ? (
        <Stack direction="row" spacing={1} alignItems="center" display="inline-flex" sx={{ maxWidth: '100%' }}>
            {noTruncation ? value : <C1TextWithTruncation
                text={value}
                variant={typographyVariant}
                textStyles={{ maxWidth: truncateWidth || 'none', flex: '1', minWidth: 0 }}
            />}
            {textToCopy && (!clicked
                ? <Tooltip title={<FormattedMessage defaultMessage="Copy value to clipboard" id="app_details_app_id_copied" />} placement="right">
                    <Box>
                        <C1IconButton
                            size="small"
                            color="inherit"
                            sx={{
                                height: '24px',
                                width: '24px',
                                opacity: 0.5,

                                '&:hover, &:focus, &:active': {
                                    backgroundColor: transparentButton ? 'transparent' : 'primary.extraLight',
                                    color: 'primary.main',
                                    opacity: 1,
                                    outline: 'none',
                                },
                            }}
                            onClick={(): void => handleClick(textToCopy)}
                            aria-label={intl.formatMessage({ id: 'component_copy_text_copy', defaultMessage: 'Copy' })}
                        >
                            <C1Icon icon={Copy} sx={{ fontSize: '14px' }} />
                        </C1IconButton>
                    </Box>
                </Tooltip>
                : <Tooltip
                    title={<FormattedMessage defaultMessage="Copied" id="app_details_app_id_copied" />}
                    placement="right"
                >
                    <Box>
                        <C1IconButton
                            size="small"
                            color="inherit"
                            sx={{
                                flexShrink: 0,
                                height: '24px',
                                width: '24px',

                                '&:hover, &:focus, &:active': {
                                    backgroundColor: transparentButton ? 'transparent' : 'primary.extraLight',
                                    color: 'primary.main',
                                    opacity: 1,
                                    outline: 'none',
                                },
                            }}
                            aria-label={intl.formatMessage({ id: 'component_copy_text_copied', defaultMessage: 'Copied' })}
                        >
                            <C1Icon icon={Check} />
                        </C1IconButton>
                    </Box>
                </Tooltip>)}
        </Stack>
    ) : <C1EmptyIndicator message={emptyText} />
}
